/**
 * Builds a non-uniform tiling for one page of items.
 *
 * This is the part that stops a template from looking like a template. An even
 * N-column grid is instantly recognisable as generated; giving the standout
 * dishes larger tiles and deriving the column count from the actual content
 * produces a page that reads as laid out on purpose. Every choice is driven by
 * the seeded PRNG, so the same menu always tiles the same way.
 */
export function synthesizeGrid(spec, items, prng) {
    const count = items.length;
    if (count === 0)
        return { columns: Math.max(1, spec.minColumns), rows: 0, cells: [] };
    const columns = chooseColumns(spec, items, prng);
    const spans = assignSpans(spec, items, columns, prng);
    const placement = placeRowMajor(spans, columns);
    repairHoles(placement);
    return placement;
}
function chooseColumns(spec, items, prng) {
    const min = Math.max(1, Math.min(spec.minColumns, spec.maxColumns));
    const max = Math.max(min, spec.maxColumns);
    const count = items.length;
    if (count <= 2)
        return Math.min(max, Math.max(min, count));
    // Long text wants fewer, wider columns; photo-led content tolerates more.
    const averageText = items.reduce((sum, item) => sum + item.textLength, 0) / count;
    const imageShare = items.filter((item) => item.hasImage).length / count;
    let target = Math.round(Math.sqrt(count * 1.15));
    if (averageText > 120)
        target -= 1;
    if (imageShare > 0.7)
        target += 1;
    const clamped = Math.max(min, Math.min(target, max));
    // One step of seeded variation, but only when the range allows it, so two
    // restaurants with similar menus still get visibly different pages.
    if (max > min && prng.chance(0.3)) {
        return clamped === max ? clamped - 1 : clamped + 1;
    }
    return clamped;
}
function assignSpans(spec, items, columns, prng) {
    const spans = items.map(() => ({ colSpan: 1, rowSpan: 1 }));
    if (columns < 2 || spec.rhythm === 'uniform')
        return spans;
    const maxSpan = Math.min(spec.maxHeroSpan, columns);
    const heroCount = Math.min(Math.floor(items.length * spec.heroRatio), Math.max(0, Math.floor(items.length / 3)));
    if (heroCount > 0 && maxSpan > 1) {
        // Rank by how much a larger tile would actually buy: a featured dish with a
        // wide photo gains most, a text-only row gains nothing.
        const ranked = items
            .map((item, index) => ({
            index,
            score: (item.featured ? 3 : 0) +
                (item.hasImage ? 2 : 0) +
                (item.imageAspect && item.imageAspect > 1.2 ? 1 : 0) +
                prng.next() * 0.9,
        }))
            .filter((entry) => items[entry.index].hasImage || items[entry.index].featured)
            .sort((a, b) => b.score - a.score)
            .slice(0, heroCount);
        for (const entry of ranked) {
            const item = items[entry.index];
            // A wide photo becomes a wide tile, a tall one a tall tile; forcing a
            // square hero onto a panoramic image just crops it badly.
            const wide = (item.imageAspect ?? 1) >= 1.1;
            spans[entry.index] = wide
                ? { colSpan: Math.min(2, maxSpan), rowSpan: 1 }
                : { colSpan: 1, rowSpan: 2 };
        }
    }
    if (spec.rhythm === 'alternating') {
        // Every third row leads with a double-width tile.
        for (let index = 0; index < spans.length; index += columns * 3) {
            spans[index] = { colSpan: Math.min(2, maxSpan), rowSpan: spans[index].rowSpan };
        }
    }
    else if (spec.rhythm === 'golden' && spans.length > 3) {
        // The opening tile carries the page, roughly at the golden-section split.
        spans[0] = { colSpan: Math.min(Math.max(1, columns - 1), maxSpan), rowSpan: 2 };
    }
    else if (spec.rhythm === 'organic') {
        for (let index = 0; index < spans.length; index += 1) {
            if (spans[index].colSpan > 1 || spans[index].rowSpan > 1)
                continue;
            if (items[index].hasImage && prng.chance(0.12)) {
                spans[index] = { colSpan: 1, rowSpan: 2 };
            }
        }
    }
    return spans;
}
/**
 * First-fit placement in reading order.
 *
 * Skyline packing would waste less space, but it reorders items visually, and a
 * menu that reads out of order is worse than a menu with a gap in it.
 */
function placeRowMajor(spans, columns) {
    const occupied = [];
    const cells = [];
    const ensureRow = (row) => {
        while (occupied.length <= row)
            occupied.push(new Array(columns).fill(false));
        return occupied[row];
    };
    const fits = (row, col, span) => {
        if (col + span.colSpan > columns)
            return false;
        for (let r = row; r < row + span.rowSpan; r += 1) {
            const line = ensureRow(r);
            for (let c = col; c < col + span.colSpan; c += 1) {
                if (line[c])
                    return false;
            }
        }
        return true;
    };
    for (const span of spans) {
        const effective = {
            colSpan: Math.min(span.colSpan, columns),
            rowSpan: span.rowSpan,
        };
        let placed = false;
        for (let row = 0; !placed && row < spans.length * 2 + 4; row += 1) {
            for (let col = 0; col <= columns - effective.colSpan; col += 1) {
                if (!fits(row, col, effective))
                    continue;
                for (let r = row; r < row + effective.rowSpan; r += 1) {
                    const line = ensureRow(r);
                    for (let c = col; c < col + effective.colSpan; c += 1)
                        line[c] = true;
                }
                cells.push({
                    colStart: col + 1,
                    colSpan: effective.colSpan,
                    rowStart: row + 1,
                    rowSpan: effective.rowSpan,
                });
                placed = true;
                break;
            }
        }
        if (!placed) {
            // Unreachable in practice, but a page that renders slightly wrong beats
            // one that throws.
            const row = occupied.length;
            ensureRow(row);
            cells.push({ colStart: 1, colSpan: 1, rowStart: row + 1, rowSpan: 1 });
            occupied[row][0] = true;
        }
    }
    return { columns, rows: occupied.length, cells };
}
/**
 * Closes gaps left by tiles that could not fit at the end of a row.
 *
 * A hole in the middle of a grid reads as a missing dish. Growing the neighbour
 * above it, or failing that the one to its left, absorbs the gap without
 * changing reading order.
 */
function repairHoles(placement) {
    const { columns, cells } = placement;
    const grid = Array.from({ length: placement.rows }, () => new Array(columns).fill(null));
    cells.forEach((cell, index) => {
        for (let r = cell.rowStart - 1; r < cell.rowStart - 1 + cell.rowSpan; r += 1) {
            for (let c = cell.colStart - 1; c < cell.colStart - 1 + cell.colSpan; c += 1) {
                if (grid[r])
                    grid[r][c] = index;
            }
        }
    });
    const neighbourAt = (row, col) => {
        if (row < 0 || row >= grid.length || col < 0 || col >= columns)
            return null;
        return grid[row][col] ?? null;
    };
    const isFree = (rowStart, rowSpan, colStart, colSpan) => {
        for (let r = rowStart; r < rowStart + rowSpan; r += 1) {
            const line = grid[r];
            if (!line)
                return false;
            for (let c = colStart; c < colStart + colSpan; c += 1) {
                if (c < 0 || c >= columns || line[c] != null)
                    return false;
            }
        }
        return true;
    };
    const claim = (index, rowStart, rowSpan, colStart, colSpan) => {
        for (let r = rowStart; r < rowStart + rowSpan; r += 1) {
            for (let c = colStart; c < colStart + colSpan; c += 1)
                grid[r][c] = index;
        }
    };
    for (let row = 0; row < placement.rows; row += 1) {
        for (let col = 0; col < columns; col += 1) {
            if (grid[row][col] != null)
                continue;
            // Each direction grows the neighbour into the gap, but only when the
            // neighbour's full opposite edge is free - a partial grow would leave the
            // tiling ragged instead of fixing it.
            const above = neighbourAt(row - 1, col);
            if (above !== null) {
                const cell = cells[above];
                if (cell.rowStart - 1 + cell.rowSpan === row &&
                    isFree(row, 1, cell.colStart - 1, cell.colSpan)) {
                    cell.rowSpan += 1;
                    claim(above, row, 1, cell.colStart - 1, cell.colSpan);
                    continue;
                }
            }
            const left = neighbourAt(row, col - 1);
            if (left !== null) {
                const cell = cells[left];
                if (cell.colStart - 1 + cell.colSpan === col &&
                    isFree(cell.rowStart - 1, cell.rowSpan, col, 1)) {
                    cell.colSpan += 1;
                    claim(left, cell.rowStart - 1, cell.rowSpan, col, 1);
                    continue;
                }
            }
            const right = neighbourAt(row, col + 1);
            if (right !== null) {
                const cell = cells[right];
                if (cell.colStart - 1 === col + 1 && isFree(cell.rowStart - 1, cell.rowSpan, col, 1)) {
                    cell.colStart -= 1;
                    cell.colSpan += 1;
                    claim(right, cell.rowStart - 1, cell.rowSpan, col, 1);
                    continue;
                }
            }
            const below = neighbourAt(row + 1, col);
            if (below !== null) {
                const cell = cells[below];
                if (cell.rowStart - 1 === row + 1 && isFree(row, 1, cell.colStart - 1, cell.colSpan)) {
                    cell.rowStart -= 1;
                    cell.rowSpan += 1;
                    claim(below, row, 1, cell.colStart - 1, cell.colSpan);
                    continue;
                }
            }
            // A gap with no growable neighbour only happens on the trailing row,
            // where it reads as deliberate whitespace rather than a missing dish.
        }
    }
}
//# sourceMappingURL=synthesize-grid.js.map